import React, { useEffect, useState } from "react";
import { Sparkles, Calendar, ArrowUp, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { celebrateNewSigils } from "../lib/sigilCelebration";

// Status → color/label map. Don't make resolved predictions disappear — recolor + relabel.
const STATUS_META = {
  pending_vote:  { label: "PROPOSED — VOTE TO PROMOTE", color: "#71717a", bg: "rgba(113,113,122,0.05)" },
  active:        { label: "ACTIVE — WAGER REPUTATION", color: "#ffd60a", bg: "rgba(255,214,10,0.06)" },
  awaiting_vote: { label: "DEADLINE PASSED — VOTE TO RESOLVE", color: "#ff9500", bg: "rgba(255,149,0,0.06)" },
  confirmed:     { label: "PREDICTED · CONFIRMED",  color: "#00ff88", bg: "rgba(0,255,136,0.06)" },
  debunked:      { label: "PREDICTED · DEBUNKED",   color: "#ff3b30", bg: "rgba(255,59,48,0.06)" },
  unresolved:    { label: "UNRESOLVED — NO CONSENSUS", color: "#a78bfa", bg: "rgba(167,139,250,0.06)" },
};

/**
 * PredictionPanel — embedded on dossier / ai_dossier / connection pages.
 * Lets any signed-in user propose a yes/no prediction with a future deadline. The community
 * upvotes to promote it to ACTIVE; users then wager reputation; on resolution winners split
 * losers' rep proportionally to their stake.
 */
export default function PredictionPanel({ targetType, targetId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [rep, setRep] = useState(null);

  const reload = () => {
    setLoading(true);
    api.get(`/predictions/target/${targetType}/${targetId}`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  const loadRep = () => {
    if (!user) return;
    api.get("/me/reputation").then((r) => setRep(r.data)).catch(() => {});
  };
  useEffect(() => { reload(); loadRep(); /* eslint-disable-next-line */ }, [targetType, targetId]);

  return (
    <section className="my-12 border border-white/10 bg-[#0a0a0a] p-6 sm:p-8" data-testid="prediction-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] text-[#ffd60a] mb-1">
            // PREDICTION MARKET · {items.length}
          </div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">
            // YES/NO QUESTIONS · STAKE REPUTATION · NO MONEY
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {user && rep && (
            <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 px-3 py-1 border border-white/10" data-testid="prediction-rep">
              REP: <span className="text-[#ffd60a]">{rep.reputation}</span>
              {rep.locked > 0 && <span className="text-zinc-500"> · LOCKED {rep.locked}</span>}
            </div>
          )}
          {user ? (
            <button onClick={() => setComposeOpen(!composeOpen)} data-testid="prediction-toggle-compose"
              className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                composeOpen ? "border border-white/15 text-zinc-400" : "bg-[#ffd60a] text-[#050505] hover:bg-white"
              }`}>
              {composeOpen ? "Cancel" : "+ Propose"}
            </button>
          ) : (
            <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">SIGN IN TO PROPOSE</span>
          )}
        </div>
      </div>

      {composeOpen && (
        <PredictionCompose targetType={targetType} targetId={targetId}
          onCreated={() => { setComposeOpen(false); reload(); }} />
      )}

      {loading ? (
        <div className="font-mono text-xs tracking-[0.2em] text-zinc-500 py-4">// LOADING…</div>
      ) : items.length === 0 ? (
        <div className="bg-[#050505] border border-white/10 p-6 text-center font-mono text-xs tracking-[0.2em] text-zinc-500">
          // NO PREDICTIONS YET — PROPOSE THE FIRST
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((p) => <PredictionRow key={p.id} p={p} user={user} onChange={() => { reload(); loadRep(); }} />)}
        </ul>
      )}
    </section>
  );
}

function PredictionRow({ p, user, onChange }) {
  const meta = STATUS_META[p.status] || STATUS_META.active;
  const deadline = p.deadline ? new Date(p.deadline) : null;
  const deadlineStr = deadline ? deadline.toLocaleDateString() : "—";
  const [wagering, setWagering] = useState(false);
  const [wagerAmount, setWagerAmount] = useState(5);
  const [wagerSide, setWagerSide] = useState("yes");

  const onPromote = async () => {
    try { await api.post(`/predictions/${p.id}/promote`); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const onWager = async () => {
    try {
      await api.post(`/predictions/${p.id}/wager`, { side: wagerSide, amount: +wagerAmount });
      toast.success(`Wagered ${wagerAmount} on ${wagerSide.toUpperCase()}`);
      setWagering(false); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const onResolveVote = async (outcome) => {
    try {
      await api.post(`/predictions/${p.id}/resolve-vote`, { outcome });
      toast.success("Vote logged"); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <li className="border p-4" data-testid={`prediction-${p.id}`}
      style={{ borderColor: `${meta.color}55`, background: meta.bg }}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <span className="font-mono text-[10px] tracking-[0.2em] px-2 py-1 border" style={{ color: meta.color, borderColor: `${meta.color}66` }}>
          {meta.label}
        </span>
        <div className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 inline-flex items-center gap-2">
          <Calendar size={10} /> {deadlineStr}
        </div>
      </div>
      <p className="text-white text-base leading-relaxed font-heading">{p.question}</p>
      <div className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 mt-2">
        proposed by @{p.creator_username}
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-2 mt-3">
        <Stat label="YES" v={p.wager_yes} color="#00ff88" mine={p.my_wager_yes} />
        <Stat label="NO" v={p.wager_no} color="#ff3b30" mine={p.my_wager_no} />
        <Stat label="WAGERS" v={p.wager_count} color="#a78bfa" />
      </div>

      {/* Actions per status */}
      {p.status === "pending_vote" && user && (
        <div className="mt-3 flex items-center gap-2">
          <button onClick={onPromote} data-testid={`promote-${p.id}`}
            className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors inline-flex items-center gap-1 ${
              p.viewer_promoted ? "bg-[#00ff88] text-[#050505]" : "border border-[#ffd60a]/40 text-[#ffd60a] hover:bg-[#ffd60a]/10"
            }`}>
            {p.viewer_promoted ? <Check size={11} /> : <ArrowUp size={11} />}
            {p.viewer_promoted ? "PROMOTED" : "PROMOTE"} · {p.promote_count}/5
          </button>
        </div>
      )}

      {p.status === "active" && user && (
        wagering ? (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <div className="flex gap-1">
              <button onClick={() => setWagerSide("yes")} type="button"
                className={`px-2 py-1 font-mono text-[10px] tracking-[0.2em] uppercase border transition-colors ${wagerSide === "yes" ? "bg-[#00ff88] text-[#050505] border-[#00ff88]" : "border-[#00ff88]/40 text-[#00ff88]"}`}>YES</button>
              <button onClick={() => setWagerSide("no")} type="button"
                className={`px-2 py-1 font-mono text-[10px] tracking-[0.2em] uppercase border transition-colors ${wagerSide === "no" ? "bg-[#ff3b30] text-[#050505] border-[#ff3b30]" : "border-[#ff3b30]/40 text-[#ff3b30]"}`}>NO</button>
            </div>
            <input type="number" min="1" max="10000" value={wagerAmount} onChange={(e) => setWagerAmount(e.target.value)} data-testid={`wager-amount-${p.id}`}
              className="w-20 bg-[#0a0a0a] border border-white/15 focus:border-[#ffd60a] outline-none px-2 py-1 font-mono text-xs text-white" />
            <button onClick={onWager} data-testid={`wager-submit-${p.id}`}
              className="px-3 py-1 font-mono text-[10px] tracking-[0.2em] uppercase bg-[#ffd60a] text-[#050505] hover:bg-white transition-colors">CONFIRM</button>
            <button onClick={() => setWagering(false)} className="px-2 text-zinc-500 hover:text-white">
              <X size={12} />
            </button>
          </div>
        ) : (
          <button onClick={() => setWagering(true)} data-testid={`wager-open-${p.id}`}
            className="mt-3 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] border border-[#ffd60a]/40 text-[#ffd60a] hover:bg-[#ffd60a]/10 transition-colors">
            STAKE REPUTATION
          </button>
        )
      )}

      {p.status === "awaiting_vote" && user && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 mr-1">VOTE:</span>
          {["confirmed", "debunked", "unresolved"].map((o) => (
            <button key={o} onClick={() => onResolveVote(o)} data-testid={`resolve-${o}-${p.id}`}
              className={`px-2 py-1 font-mono text-[10px] tracking-[0.2em] uppercase border transition-colors ${
                p.my_resolve_vote === o
                  ? "bg-[#a78bfa] text-[#050505] border-[#a78bfa]"
                  : "border-white/20 text-zinc-300 hover:border-white/50"
              }`}>{o}</button>
          ))}
        </div>
      )}
    </li>
  );
}

function Stat({ label, v, color, mine }) {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-2 text-center">
      <div className="font-heading text-xl" style={{ color }}>{v ?? 0}</div>
      <div className="font-mono text-[8px] tracking-[0.25em] text-zinc-500">{label}</div>
      {mine ? <div className="font-mono text-[8px] tracking-[0.15em] text-zinc-400 mt-0.5">YOU: {mine}</div> : null}
    </div>
  );
}

function PredictionCompose({ targetType, targetId, onCreated }) {
  const [question, setQuestion] = useState("");
  const [deadline, setDeadline] = useState(() => {
    const d = new Date(); d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (question.trim().length < 10) { toast.error("Question too short"); return; }
    setBusy(true);
    try {
      await api.post("/predictions", {
        target_type: targetType, target_id: targetId,
        question: question.trim(),
        deadline: new Date(deadline + "T23:59:59Z").toISOString(),
      });
      toast.success("Prediction proposed — needs 5 community upvotes to go active");
      setQuestion(""); onCreated?.();
      // Posting predictions unlocks prophet / oracle sigils
      celebrateNewSigils().catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="border border-[#ffd60a]/40 bg-[#0a0a0a] p-5 mb-4" data-testid="prediction-form">
      <input value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={240}
        data-testid="prediction-question-input"
        placeholder="Yes/no falsifiable question — e.g. 'Will all JFK files be declassified by Dec 31, 2027?'"
        className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#ffd60a] outline-none px-3 py-2 font-mono text-sm text-white transition-colors mb-3" />
      <div className="flex items-center gap-3 flex-wrap">
        <label className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 inline-flex items-center gap-2">
          DEADLINE
          <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} data-testid="prediction-deadline-input"
            className="bg-[#0a0a0a] border border-white/15 focus:border-[#ffd60a] outline-none px-2 py-1 font-mono text-xs text-white" />
        </label>
        <button disabled={busy} type="submit" data-testid="prediction-submit"
          className="ml-auto px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] bg-[#ffd60a] text-[#050505] hover:bg-white transition-colors disabled:opacity-50">
          {busy ? "POSTING…" : "PROPOSE"}
        </button>
      </div>
    </form>
  );
}
